import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Input from './Input';
import { useAnchoredPosition } from '../hooks/useAnchoredPosition';

/* ── colour math (hex ⇄ hsv) ──────────────────────────────────────── */

const HEX_RE = /^#[0-9A-F]{6}$/;
const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/** Normalise free text to `#RRGGBB` (uppercase, prepend #) or null if invalid. */
function normalizeHex(raw: string): string | null {
  let x = raw.trim().toUpperCase();
  if (!x) return null;
  if (!x.startsWith('#')) x = `#${x}`;
  return HEX_RE.test(x) ? x : null;
}

interface Hsv {
  h: number; // 0-360
  s: number; // 0-1
  v: number; // 0-1
}

function hexToRgb(hex: string): [number, number, number] {
  const x = normalizeHex(hex) ?? '#000000';
  return [parseInt(x.slice(1, 3), 16), parseInt(x.slice(3, 5), 16), parseInt(x.slice(5, 7), 16)];
}

function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => Math.round(clamp01(n / 255) * 255).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}

function hexToHsv(hex: string): Hsv {
  let [r, g, b] = hexToRgb(hex);
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

function hsvToHex({ h, s, v }: Hsv): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0; let g = 0; let b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

/* ── component ────────────────────────────────────────────────────── */

export interface ColorPickerPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  /** Initial `#RRGGBB`. */
  initialColor: string;
  /** Called with the chosen `#RRGGBB` when the user commits (Enter / Apply /
   *  click outside). NOT called on cancel (Escape). */
  onCommit: (hex: string) => void;
  /** Called after commit or cancel — the parent should unmount the popover. */
  onClose: () => void;
  /** Label for the Apply button (i18n). Default Spanish. */
  applyLabel?: string;
}

/**
 * BolloPrint-styled colour picker popover: a saturation/value area + hue slider
 * + hex field, anchored to a trigger and portalled. The choice is STAGED —
 * dragging / typing only previews; the value is committed once (Enter, the
 * Apply button, or a click outside), so it never spams the caller per pointer
 * move (the bug with the native `<input type=color>`). Escape cancels.
 * Positioning uses the shared `useAnchoredPosition` (flips up when clipped).
 */
export default function ColorPickerPopover({
  anchorRef,
  initialColor,
  onCommit,
  onClose,
  applyLabel = 'Aplicar',
}: ColorPickerPopoverProps) {
  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(initialColor));
  const [hexText, setHexText] = useState<string>(() => normalizeHex(initialColor) ?? '#888888');
  const popRef = useRef<HTMLDivElement>(null);
  const svRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);

  const pos = useAnchoredPosition(anchorRef, true, {
    estimatedHeight: 280,
    estimatedWidth: 288,
    gap: 6,
  });

  const currentHex = normalizeHex(hexText) ?? hsvToHex(hsv);

  // Keep an always-fresh commit fn for the document listeners below.
  const commitRef = useRef<() => void>(() => {});
  commitRef.current = () => {
    onCommit(normalizeHex(hexText) ?? hsvToHex(hsv));
    onClose();
  };

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      commitRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') { e.preventDefault(); commitRef.current(); }
      else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (next: Hsv) => { setHsv(next); setHexText(hsvToHex(next)); };

  const applySV = (cx: number, cy: number) => {
    const r = svRef.current?.getBoundingClientRect();
    if (!r) return;
    apply({ ...hsv, s: clamp01((cx - r.left) / r.width), v: clamp01(1 - (cy - r.top) / r.height) });
  };
  const applyHue = (cx: number) => {
    const r = hueRef.current?.getBoundingClientRect();
    if (!r) return;
    apply({ ...hsv, h: clamp01((cx - r.left) / r.width) * 360 });
  };

  const startDrag = (move: (cx: number, cy: number) => void) => (e: React.PointerEvent) => {
    e.preventDefault();
    move(e.clientX, e.clientY);
    const onMove = (ev: PointerEvent) => move(ev.clientX, ev.clientY);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const hueHex = hsvToHex({ h: hsv.h, s: 1, v: 1 });

  return createPortal(
    <div
      ref={popRef}
      style={{ top: pos?.top, bottom: pos?.bottom, left: pos?.left, visibility: pos ? 'visible' : 'hidden' }}
      className={`fixed z-[200] w-72 rounded-xl border border-border-subtle dark:border-border-subtle-dark bg-white dark:bg-surface-dark p-3 shadow-xl animate-in fade-in zoom-in-95 duration-150 ${pos?.flip ? 'origin-bottom' : 'origin-top'}`}
    >
      {/* Saturation / value area */}
      <div
        ref={svRef}
        onPointerDown={startDrag(applySV)}
        className="relative h-36 w-full cursor-crosshair rounded-lg border border-border-subtle dark:border-border-subtle-dark touch-none select-none"
        style={{
          background: `linear-gradient(to top, #000, rgba(0,0,0,0)), linear-gradient(to right, #fff, rgba(255,255,255,0)), ${hueHex}`,
        }}
      >
        <span
          className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
          style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, boxShadow: '0 0 0 1px rgba(0,0,0,.4)' }}
        />
      </div>

      {/* Hue slider */}
      <div
        ref={hueRef}
        onPointerDown={startDrag((cx) => applyHue(cx))}
        className="relative mt-3 h-3 w-full cursor-pointer rounded-full touch-none select-none"
        style={{
          background:
            'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
        }}
      >
        <span
          className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-white shadow"
          style={{ left: `${(hsv.h / 360) * 100}%`, boxShadow: '0 0 0 1px rgba(0,0,0,.4)' }}
        />
      </div>

      {/* Hex field + preview + apply */}
      <div className="mt-3 flex items-end gap-2">
        <span
          className="h-9 w-9 shrink-0 rounded-md border border-border-subtle dark:border-border-subtle-dark shadow-sm"
          style={{ backgroundColor: currentHex }}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <Input
            size="compact"
            value={hexText}
            maxLength={7}
            placeholder="#RRGGBB"
            aria-label="HEX"
            onChange={(e) => {
              const txt = e.target.value;
              setHexText(txt);
              const h = normalizeHex(txt);
              if (h) setHsv(hexToHsv(h));
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => commitRef.current()}
          className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-primary/90"
        >
          {applyLabel}
        </button>
      </div>
    </div>,
    document.body,
  );
}
